import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TopNav from '../../components/layout/TopNav';
import Tooltip from '../../components/shared/Tooltip';
import api from '../../services/api';
import type { Video, CoachingNote } from '../../types';
import { UserRole, ScoringStatus } from '../../types';
import { useAuthStore, useUploadStore } from '../../store';
import { useProcessingPoller } from '../../hooks/useProcessingPoller';

interface PipelineStep {
  step: string;
  success: boolean;
  rawOutput: Record<string, unknown>;
  error?: string;
}

interface PipelineData {
  steps: PipelineStep[];
  hasHardBlocks: boolean;
  hasErrors: boolean;
  abortedAtStep?: string;
}

interface EvaluatorFlag {
  flagged: boolean;
  flag_type: string;
  severity: string;
  transcript_excerpt: string | null;
  details: Record<string, unknown> | null;
  timestamp_start?: string;
  timestamp_end?: string;
  timestamp_start_seconds?: number;
  timestamp_end_seconds?: number;
}

interface DbComplianceFlag {
  id: string;
  flagType: string;
  severity: string;
  isResolved: boolean;
  transcriptExcerpt: string | null;
  detectedAt: string;
}

interface EvaluatorCompliance {
  total_checks: number;
  total_flagged: number;
  hard_blocks: number;
  soft_warnings: number;
  can_proceed_to_scoring: boolean;
  flags: EvaluatorFlag[];
}

interface EvaluatorOutput {
  video_source?: string;
  duration_seconds?: number;
  has_transcript?: boolean;
  transcript?: string;
  compliance?: EvaluatorCompliance;
  verdict?: string;
  verdict_reason?: string | null;
}

const GRADE_COLORS: Record<string, string> = {
  'A': 'bg-emerald-500 text-white shadow-emerald-500/20',
  'B': 'bg-blue-500 text-white shadow-blue-500/20',
  'C': 'bg-amber-500 text-white shadow-amber-500/20',
  'D': 'bg-orange-500 text-white shadow-orange-500/20',
  'F': 'bg-red-500 text-white shadow-red-500/20',
};

const SEVERITY_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  HARD_BLOCK: {
    bg: 'bg-red-50 dark:bg-red-950/10',
    text: 'text-red-600 dark:text-red-400',
    border: 'border-red-100 dark:border-red-900/20'
  },
  SOFT_WARNING: {
    bg: 'bg-amber-50 dark:bg-amber-950/10',
    text: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-100 dark:border-amber-900/20'
  },
};

/* ---------- Transcript Modal ---------- */
const TranscriptModal: React.FC<{ transcript: string; onClose: () => void }> = ({ transcript, onClose }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(transcript).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [transcript]);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm" />
      <div
        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 w-full max-w-3xl rounded-xl shadow-2xl flex flex-col relative overflow-hidden max-h-[85vh] animate-in fade-in zoom-in duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight uppercase tracking-widest">Transcript Analysis</h2>
            <p className="text-[10px] font-black uppercase text-slate-400 dark:text-slate-600 tracking-widest mt-1">Full Speech Extraction</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-primary transition-all font-bold text-[10px] tracking-widest uppercase border border-slate-100 dark:border-slate-700"
            >
              <span className="material-symbols-outlined text-[18px]">{copied ? 'check' : 'content_copy'}</span>
              {copied ? 'Copied' : 'Copy Text'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-10 h-10 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all border border-slate-100 dark:border-slate-800"
            >
              <span className="material-symbols-outlined text-[20px]">close</span>
            </button>
          </div>
        </div>
        {/* Body */}
        <div className="px-8 py-8 overflow-y-auto custom-scrollbar flex-1">
          <p className="text-sm font-medium text-slate-600 dark:text-slate-400 leading-relaxed whitespace-pre-wrap text-justify">
            {transcript}
          </p>
        </div>
      </div>
    </div>
  );
};

const SIGNAL_NAMES: Record<number, string> = {
  1: 'Zone Coverage', 2: 'Face On Camera', 3: 'Lighting', 4: 'Stability',
  5: 'Duration', 6: 'Speech Pace', 7: 'Energy', 8: 'Buyer Name',
  9: 'Features', 10: 'Call to Action',
};

const VideoDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userRole = useAuthStore((s) => s.user?.role);
  const userId = useAuthStore((s) => s.user?.id);
  const dismissByVideoId = useUploadStore((s) => s.dismissByVideoId);
  const [video, setVideo] = useState<Video | null>(null);
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Coaching state
  const [coachingNotes, setCoachingNotes] = useState<CoachingNote[]>([]);
  const [coachingLoading, setCoachingLoading] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [acknowledging, setAcknowledging] = useState<string | null>(null);

  const [dbFlags, setDbFlags] = useState<DbComplianceFlag[] | null>(null);

  const [rescoring, setRescoring] = useState(false);
  const [rescoreMessage, setRescoreMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [outcome, setOutcome] = useState<{
    outcome: string | null;
    confidence: number;
    saleDate: string | null;
  } | null>(null);

  const seekTo = useCallback((seconds: number) => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = seconds;
    el.play().catch(() => { /* autoplay may be blocked */ });
    // Scroll video into view
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await api.delete(`/videos/${id}`);
      dismissByVideoId(id);
      navigate('/videos', { replace: true });
    } catch {
      setError('Failed to delete video.');
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleRescore = async () => {
    if (!id) return;
    setRescoring(true);
    setRescoreMessage(null);
    try {
      await api.post(`/videos/${id}/rescore`, {});
      setRescoreMessage({ type: 'success', text: 'Video queued for rescoring. Results will update shortly.' });
      setTimeout(() => fetchVideo(), 1500);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Failed to queue rescore. Please try again.';
      setRescoreMessage({ type: 'error', text: message });
    } finally {
      setRescoring(false);
    }
  };

  const isGroupAdmin = userRole === UserRole.GROUP_ADMIN;
  const videoBase = isGroupAdmin ? `/group-admin/videos/${id}` : `/videos/${id}`;

  const fetchVideo = useCallback(async () => {
    if (!id) return;
    try {
      const [videoRes, streamRes] = await Promise.all([
        api.get(videoBase),
        api.get(`${videoBase}/stream`).catch(() => null),
      ]);
      setVideo(videoRes.data.data ?? videoRes.data);
      if (streamRes) {
        setStreamUrl(streamRes.data.data?.url ?? null);
      }
    } catch {
      setError('Failed to load video details.');
    } finally {
      setLoading(false);
    }
  }, [id, videoBase]);

  const fetchOutcome = useCallback(async () => {
    if (!id || isGroupAdmin) return;
    try {
      const res = await api.get(`/reports/outcome-correlation/video/${id}`);
      const data = res.data.data as
        | {
            correlation: { correlationConfidence: string };
            dms: { outcome: string | null; saleDate: string | null };
          }
        | null;
      if (data) {
        setOutcome({
          outcome: data.dms.outcome,
          confidence: Number(data.correlation.correlationConfidence),
          saleDate: data.dms.saleDate,
        });
      } else {
        setOutcome(null);
      }
    } catch {
      setOutcome(null);
    }
  }, [id, isGroupAdmin]);

  const fetchDbFlags = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get(`${videoBase}/compliance`);
      setDbFlags(res.data.data ?? []);
    } catch {
      setDbFlags([]);
    }
  }, [id, videoBase]);

  const fetchCoachingNotes = useCallback(async () => {
    if (!id || isGroupAdmin) return;
    setCoachingLoading(true);
    try {
      const res = await api.get(`/coaching/video/${id}`);
      setCoachingNotes(res.data.data ?? []);
    } catch {
      // coaching not enabled or error — silently ignore
    } finally {
      setCoachingLoading(false);
    }
  }, [id, isGroupAdmin]);

  const handleAddNote = async () => {
    if (!id || newNoteText.trim().length < 20) return;
    setSubmittingNote(true);
    try {
      const res = await api.post(`/coaching/${id}/note`, { noteText: newNoteText.trim() });
      setCoachingNotes((prev) => [res.data.data, ...prev]);
      setNewNoteText('');
    } catch {
      // silently ignore
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleAcknowledge = async (noteId: string) => {
    setAcknowledging(noteId);
    try {
      const res = await api.post(`/coaching/${noteId}/acknowledge`);
      setCoachingNotes((prev) => prev.map((n) => n.id === noteId ? res.data.data : n));
    } catch {
      // silently ignore
    } finally {
      setAcknowledging(null);
    }
  };

  useEffect(() => {
    fetchVideo();
  }, [fetchVideo]);

  useEffect(() => {
    fetchDbFlags();
  }, [fetchDbFlags]);

  useEffect(() => {
    fetchCoachingNotes();
  }, [fetchCoachingNotes]);

  useEffect(() => {
    fetchOutcome();
  }, [fetchOutcome]);

  // Auto-refresh when this video finishes processing in the background
  useProcessingPoller(video ? [video] : [], fetchVideo);

  const pipeline = video?.evaluatorRawResponse?.pipeline as (PipelineData & Partial<EvaluatorOutput>) | undefined;
  const evaluatedAt = video?.evaluatorRawResponse?.evaluatedAt as string | undefined;

  // Extract evaluator output — supports both legacy steps[] shape and current flat shape
  const evaluatorOutput: EvaluatorOutput | null = (() => {
    if (!pipeline) return null;
    // Legacy: pipeline.steps[0].rawOutput
    if (pipeline.steps?.length) {
      const step = pipeline.steps[0];
      if (step.success && step.rawOutput) return step.rawOutput as unknown as EvaluatorOutput;
    }
    // Current: pipeline is the output directly
    if (pipeline.verdict !== undefined) return pipeline as unknown as EvaluatorOutput;
    return null;
  })();
  const compliance = evaluatorOutput?.compliance ?? null;

  // video.scoringStatus is the authoritative verdict — it is set to 'FLAGGED' by the backend
  // whenever ANY hard block exists, including VIP PII flags that are written after the Python
  // evaluator has already produced its own verdict. evaluatorOutput.verdict alone is not
  // reliable because it only reflects what the evaluator saw before VIP ran.
  const effectiveVerdict = video?.scoringStatus === 'FLAGGED'
    ? 'FAIL'
    : evaluatorOutput?.verdict ?? null;

  // DB flags are authoritative — they include VIP-sourced flags (e.g. PII_DETECTED) that
  // the Python evaluator never sees. Use them for counts and the flag list.
  const activeDbFlags = (dbFlags ?? []).filter((f) => !f.isResolved);
  const dbHardBlocks = activeDbFlags.filter((f) => f.severity === 'HARD_BLOCK').length;
  const dbSoftWarnings = activeDbFlags.filter((f) => f.severity === 'SOFT_WARNING').length;
  // Build a lookup of evaluator flags for timestamp/excerpt enrichment
  const evaluatorFlagByType = new Map(
    (compliance?.flags ?? []).filter((f) => f.flagged).map((f) => [f.flag_type, f]),
  );

  // Reason to show under the FAIL banner — prefer evaluator's own reason (set when Python
  // evaluator caught the block), fall back to DB block count (covers VIP-sourced flags
  // like PII_DETECTED that the evaluator never sees).
  const effectiveVerdictReason = effectiveVerdict === 'FAIL'
    ? (evaluatorOutput?.verdict_reason ?? (dbHardBlocks > 0
        ? `${dbHardBlocks} hard block${dbHardBlocks > 1 ? 's' : ''} detected`
        : null))
    : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 flex flex-col transition-colors duration-300">
        <TopNav />
        <main className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent animate-spin rounded-full" />
            <span className="text-sm font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Loading Evaluation...</span>
          </div>
        </main>
      </div>
    );
  }

  if (error || !video) {
    return (
      <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 flex flex-col transition-colors duration-300">
        <TopNav />
        <main className="flex-1 flex flex-col items-center justify-center gap-6">
          <div className="w-20 h-20 rounded-2xl bg-red-50 dark:bg-red-900/10 flex items-center justify-center text-red-500">
            <span className="material-symbols-outlined text-4xl">error</span>
          </div>
          <div className="text-center">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Video Not Found</h2>
            <p className="text-slate-500 dark:text-slate-400">{error ?? 'The requested video could not be retrieved.'}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-8 py-3 rounded-xl bg-primary text-white font-bold shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all"
          >
            Go Back
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 transition-all duration-300 font-sans flex flex-col">
      <TopNav />
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-12">
        {/* Top Header & Actions */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-11 h-11 rounded-xl flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-primary dark:hover:text-primary hover:bg-white dark:hover:bg-slate-900 transition-all cursor-pointer border border-slate-200 dark:border-slate-800"
            >
              <span className="material-symbols-outlined text-[20px]">arrow_back</span>
            </button>
          </div>

          {!isGroupAdmin && (
            <div className="flex items-center gap-3">
              {userRole === UserRole.SALES_REP &&
                (video.scoringStatus === ScoringStatus.FLAGGED || video.scoringStatus === ScoringStatus.PENDING) && (
                <button
                  type="button"
                  onClick={handleRescore}
                  disabled={rescoring}
                  className="h-11 px-6 font-bold text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900/30 hover:bg-amber-50 dark:hover:bg-amber-950/40 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer flex items-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100"
                >
                  {rescoring ? (
                    <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent animate-spin rounded-full" />
                  ) : (
                    <span className="material-symbols-outlined text-[20px]">refresh</span>
                  )}
                  <span className="text-sm tracking-tight">{rescoring ? 'Rescoring…' : 'Rescore'}</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="h-11 px-6 rounded-xl font-bold text-red-500 dark:text-red-400 bg-red-50/50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/30 hover:bg-red-50 dark:hover:bg-red-950/40 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer flex items-center gap-2 shadow-sm"
              >
                <span className="material-symbols-outlined text-[20px]">delete</span>
                <span className="text-sm tracking-tight">Delete</span>
              </button>
            </div>
          )}
        </div>

        {rescoreMessage && (
          <div className={`mb-6 px-5 py-3 flex items-center gap-3 text-sm font-semibold border ${
            rescoreMessage.type === 'success'
              ? 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 border-emerald-100 dark:border-emerald-900/30'
              : 'bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400 border-red-100 dark:border-red-900/30'
          }`}>
            <span className="material-symbols-outlined text-[18px]">
              {rescoreMessage.type === 'success' ? 'check_circle' : 'error'}
            </span>
            {rescoreMessage.text}
            <button
              type="button"
              onClick={() => setRescoreMessage(null)}
              className="ml-auto text-current opacity-60 hover:opacity-100 transition-opacity"
            >
              <span className="material-symbols-outlined text-[16px]">close</span>
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Left: Video Player + Info */}
          <div className="xl:col-span-2 space-y-8">
            {/* Video Player */}
            <div className="bg-black/90 dark:bg-slate-900/60 border border-slate-200 dark:border-white/5 rounded-2xl shadow-2xl overflow-hidden group relative">
              <div className="aspect-video w-full bg-slate-950 flex items-center justify-center">
                {streamUrl ? (
                  <video
                    ref={videoRef}
                    src={streamUrl}
                    controls
                    className="w-full h-full object-contain"
                    preload="metadata"
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="flex flex-col items-center gap-4 text-slate-600 dark:text-slate-400">
                    <span className="material-symbols-outlined text-6xl">videocam_off</span>
                    <p className="font-bold text-sm uppercase tracking-widest">Video Stream Unavailable</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight whitespace-normal break-words max-w-2xl">
                {video.title}
              </h1>
              {outcome && outcome.outcome && (
                <Tooltip content={`Match confidence ${(outcome.confidence * 100).toFixed(0)}%`}>
                <span
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                    outcome.outcome === 'CLOSED'
                      ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400'
                      : outcome.outcome === 'APPOINTMENT_SET'
                        ? 'bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400'
                        : outcome.outcome === 'LOST'
                          ? 'bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-500'
                  }`}
                >
                  <span className="material-symbols-outlined text-[14px]">insights</span>
                  {outcome.outcome}
                  {outcome.saleDate && (
                    <span className="opacity-70">
                      · {new Date(outcome.saleDate).toLocaleDateString()}
                    </span>
                  )}
                </span>
                </Tooltip>
              )}
            </div>

            {/* Chapter Markers */}
            {(() => {
              const activeFlagTypes = new Set(activeDbFlags.map((f) => f.flagType));
              const flagsWithTime = (compliance?.flags ?? []).filter(
                (f) => f.flagged && f.timestamp_start_seconds != null && activeFlagTypes.has(f.flag_type),
              );
              const duration = evaluatorOutput?.duration_seconds ?? 0;
              if (flagsWithTime.length === 0 || duration <= 0) return null;

              return (
                <div className="bg-white/40 dark:bg-slate-900/40 border border-slate-200 dark:border-white/5 rounded-2xl p-8 shadow-sm">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center text-red-500">
                       <span className="material-symbols-outlined text-[24px]">flag</span>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-widest">Incident Timeline</h3>
                  </div>

                  <div className="relative h-2 bg-slate-100 dark:bg-slate-800 rounded-full w-full mb-8 overflow-hidden">
                    {flagsWithTime.map((flag, i) => {
                      const startPct = ((flag.timestamp_start_seconds ?? 0) / duration) * 100;
                      const endPct = ((flag.timestamp_end_seconds ?? flag.timestamp_start_seconds ?? 0) / duration) * 100;
                      const widthPct = Math.max(endPct - startPct, 1.5);
                      const isHard = flag.severity === 'HARD_BLOCK';
                      return (
                        <Tooltip content={`${flag.flag_type.replace(/_/g, ' ')} at ${flag.timestamp_start ?? ''}`} placement="bottom">
                          <button
                            type="button"
                            key={i}
                            onClick={() => seekTo(flag.timestamp_start_seconds ?? 0)}
                            className={`absolute top-0 h-full cursor-pointer transition-all hover:scale-y-110 ${isHard ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)]' : 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.4)]'
                              }`}
                            style={{ left: `${startPct}%`, width: `${widthPct}%`, minWidth: '4px' }}
                          />
                        </Tooltip>
                      );
                    })}
                  </div>

                  <div className="space-y-3">
                    {flagsWithTime.map((flag, i) => {
                      const styles = SEVERITY_STYLES[flag.severity] ?? SEVERITY_STYLES['HARD_BLOCK'];
                      return (
                        <button
                          type="button"
                          key={i}
                          onClick={() => seekTo(flag.timestamp_start_seconds ?? 0)}
                          className={`w-full flex items-center gap-4 p-4 rounded-2xl border text-left transition-all hover:bg-white dark:hover:bg-slate-800/50 hover:shadow-sm ${styles.bg} ${styles.border}`}
                        >
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-white dark:bg-slate-900 border ${styles.border} ${styles.text}`}>
                            <span className="material-symbols-outlined text-[18px]">
                              {flag.severity === 'HARD_BLOCK' ? 'gpp_bad' : 'warning'}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className={`block text-[10px] font-black tracking-widest uppercase ${styles.text}`}>
                                {flag.flag_type.replace(/_/g, ' ')}
                              </span>
                              <span className="font-mono text-[10px] font-bold text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800 px-2 py-0.5 rounded-lg border border-slate-100 dark:border-slate-700">
                                {flag.timestamp_start ?? '—'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-bold text-primary uppercase tracking-tight">Review Fragment</span>
                              <span className="material-symbols-outlined text-[14px] text-primary">play_circle</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Video Context Grid - Consolidated */}
            <div className="bg-white/40 dark:bg-slate-900/40 border border-slate-200 dark:border-white/5 rounded-2xl p-8 shadow-sm">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { label: 'Evaluation Status', value: video.scoringStatus, icon: 'analytics', color: video.scoringStatus === 'SCORED' ? 'text-emerald-500' : 'text-primary' },
                  { label: 'Upload Timeline', value: new Date(video.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }), icon: 'calendar_today' },
                  { label: 'Asset Payload', value: video.fileSizeBytes ? `${(Number(video.fileSizeBytes) / (1024 * 1024)).toFixed(1)} MB` : '-', icon: 'database' },
                  {
                    label: 'Customer Alias',
                    value: video.buyerFirstName
                      ? `${video.buyerFirstName}${video.crmBuyerNameFetched ? ' ✦ CRM' : ''}`
                      : '-',
                    icon: 'person',
                  },
                ].map((item, idx) => (
                  <div key={item.label} className={`flex flex-col gap-1 ${idx !== 0 ? 'lg:border-l lg:border-slate-100 lg:dark:border-slate-800 lg:pl-6' : ''}`}>
                    <div className="flex items-center gap-2 text-slate-400 dark:text-slate-600 uppercase">
                      <span className="material-symbols-outlined text-[16px]">{item.icon}</span>
                      <span className="text-[9px] font-black tracking-widest">{item.label}</span>
                    </div>
                    <div className={`text-sm font-bold truncate ${item.color || 'text-slate-900 dark:text-white'}`}>
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Score Breakdown Section */}
            {video.videoScore && (
              <div className="bg-white/40 dark:bg-slate-900/40 border border-slate-200 dark:border-white/5 rounded-2xl p-8 shadow-sm">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary">
                      <span className="material-symbols-outlined text-[24px]">grade</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight uppercase tracking-widest">Score Performance</h3>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-sm font-black text-slate-900 dark:text-white">{video.videoScore.compositeScore}</div>
                      <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{video.videoScore.gradeLabel}</div>
                    </div>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl font-black shadow-lg shadow-primary/10 ${GRADE_COLORS[video.videoScore.grade] ?? ''}`}>
                      {video.videoScore.grade}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {([
                    {
                      label: 'Coverage',
                      value: video.videoScore.signalZoneCoverage,
                      weight: '20%',
                      ranges: [
                        { range: '6/6 zones', score: '100' },
                        { range: '5/6 zones', score: '83' },
                        { range: '4/6 zones', score: '67' },
                        { range: '3/6 zones', score: '50' },
                      ],
                      logic: 'Detects 6 vehicle zones: exterior front/rear, driver/passenger interior, engine bay, wheels. Score = (zones detected / 6) × 100.',
                    },
                    {
                      label: 'Face Detection',
                      value: video.videoScore.signalFaceOnCamera,
                      weight: '10%',
                      ranges: [
                        { range: 'Face in >20% of frames', score: '100' },
                        { range: 'Face in ≤20% of frames', score: '0' },
                      ],
                      logic: 'Binary. Salesperson must be on camera for more than 20% of video frames.',
                    },
                    {
                      label: 'Lighting',
                      value: video.videoScore.signalLighting,
                      weight: '10%',
                      ranges: [
                        { range: '80+ lux, even distribution', score: '71–100' },
                        { range: '40–80 lux', score: '31–70' },
                        { range: '<40 lux', score: '0–30' },
                        { range: 'Harsh shadows penalty', score: '−15' },
                        { range: 'Glare penalty', score: '−10' },
                      ],
                      logic: 'Measures average luminance across sampled frames. Penalises high shadow variance and luminance outliers (glare).',
                    },
                    {
                      label: 'Stability',
                      value: video.videoScore.signalStability,
                      weight: '5%',
                      ranges: [
                        { range: 'Low variance (stable)', score: '80–100' },
                        { range: 'Moderate variance', score: '40–79' },
                        { range: 'High erratic variance', score: '0–39' },
                      ],
                      logic: 'Measures inter-frame optical flow variance. Smooth panning is acceptable; erratic shake is penalised.',
                    },
                    {
                      label: 'Duration',
                      value: video.videoScore.signalDuration,
                      weight: '5%',
                      ranges: [
                        { range: '90–180 seconds', score: '100 (optimal)' },
                        { range: '181–240 seconds', score: '80' },
                        { range: '60–89 seconds', score: '70' },
                        { range: '>240 seconds', score: '50' },
                        { range: '<60 seconds', score: '30' },
                      ],
                      logic: 'Based on video length. Optimal range is 90–180 seconds for buyer engagement.',
                    },
                    {
                      label: 'Speech Rate',
                      value: video.videoScore.signalSpeechPace,
                      weight: '10%',
                      ranges: [
                        { range: '120–160 WPM', score: '100 (optimal)' },
                        { range: '100–119 or 161–180 WPM', score: '75' },
                        { range: '<100 WPM', score: '60' },
                        { range: '>180 WPM', score: '50' },
                        { range: 'Filler density 10–15%', score: '−10' },
                        { range: 'Filler density >15%', score: '−20' },
                      ],
                      logic: 'Scores words-per-minute then applies a filler word density penalty (um, uh, like, you know, basically, etc.).',
                    },
                    {
                      label: 'Vocal Energy',
                      value: video.videoScore.signalEnergy,
                      weight: '15%',
                      ranges: [
                        { range: 'High excitement + confidence', score: '80–100' },
                        { range: 'Moderate energy', score: '50–79' },
                        { range: 'Low energy', score: '0–49' },
                      ],
                      logic: 'audEERING prosody analysis averaging Excitement, Enthusiasm, and Confidence dimensions across all speech segments in the video.',
                    },
                    {
                      label: 'Buyer Name',
                      value: video.videoScore.signalBuyerName,
                      weight: '10%',
                      ranges: [
                        { range: 'Mentioned 2+ times', score: '100' },
                        { range: 'Mentioned 1 time', score: '60' },
                        { range: 'Not mentioned', score: '0' },
                      ],
                      logic: 'Checks if the buyer\'s first name is mentioned in the transcript. Case-insensitive whole-word match.',
                    },
                    {
                      label: 'Feature Recall',
                      value: video.videoScore.signalFeatureCallout,
                      weight: '5%',
                      ranges: [
                        { range: '5–6 categories covered', score: '100' },
                        { range: '3–4 categories covered', score: '75' },
                        { range: '1–2 categories covered', score: '40' },
                        { range: '0 categories', score: '0' },
                      ],
                      logic: 'Counts distinct feature categories mentioned: Technology, Safety, Performance, Comfort, Cargo, Warranty.',
                    },
                    {
                      label: 'Action CTA',
                      value: video.videoScore.signalCta,
                      weight: '10%',
                      ranges: [
                        { range: 'CTA phrase detected', score: '100' },
                        { range: 'No CTA phrase', score: '0' },
                      ],
                      logic: 'Binary. Checks the final 30 seconds for a call-to-action phrase (e.g. "come in", "schedule a test drive", "give me a call", "I\'ll hold this").',
                    },
                  ] as Array<{ label: string; value: number; weight: string; ranges: { range: string; score: string }[]; logic: string }>).map((signal) => (
                    <div key={signal.label} className="relative group bg-slate-50/50 dark:bg-white/5 border border-slate-100 dark:border-white/5 rounded-2xl p-4 transition-all hover:border-primary/20 cursor-default">
                      <span className="block text-[9px] font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest mb-1 truncate">{signal.label}</span>
                      <span className="text-2xl font-black text-slate-900 dark:text-white tracking-tighter">{signal.value}</span>
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-64 z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 p-4 text-left">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-widest">{signal.label}</span>
                            <span className="text-[10px] font-black text-primary uppercase tracking-widest">{signal.weight}</span>
                          </div>
                          <p className="text-[10px] text-slate-500 dark:text-slate-400 mb-3 leading-relaxed">{signal.logic}</p>
                          <div className="border-t border-slate-100 dark:border-white/5 pt-2 space-y-1.5">
                            {signal.ranges.map((r) => (
                              <div key={r.range} className="flex items-start justify-between gap-3">
                                <span className="text-[10px] text-slate-500 dark:text-slate-400 leading-tight">{r.range}</span>
                                <span className="text-[10px] font-black text-slate-900 dark:text-white whitespace-nowrap">{r.score}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Arrow */}
                        <div className="w-2.5 h-2.5 bg-white dark:bg-slate-900 border-r border-b border-slate-200 dark:border-white/5 rotate-45 mx-auto -mt-1.5" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Evaluator Results + Compliance */}
          <div className="xl:col-span-1 space-y-8">
            {/* Verdict Banner */}
            {(evaluatorOutput || video?.scoringStatus === 'FLAGGED') && (
              <div className={`p-6 rounded-2xl border flex items-center gap-5 transition-all shadow-sm ${effectiveVerdict === 'FAIL'
                  ? 'bg-red-50 dark:bg-red-950/10 border-red-100 dark:border-red-900/20 text-red-600 dark:text-red-400'
                  : pipeline?.hasErrors
                    ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-100 dark:border-amber-900/20 text-amber-600 dark:text-amber-400'
                    : 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-100 dark:border-emerald-900/20 text-emerald-600 dark:text-emerald-400'
                }`}>
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${effectiveVerdict === 'FAIL' ? 'bg-red-500/10' :
                    pipeline?.hasErrors ? 'bg-amber-500/10' : 'bg-emerald-500/10'
                  }`}>
                  <span className="material-symbols-outlined text-[32px]">
                    {effectiveVerdict === 'FAIL' ? 'gpp_bad' : pipeline?.hasErrors ? 'warning' : 'verified'}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="text-lg font-bold tracking-tight uppercase tracking-widest">
                    {effectiveVerdict === 'FAIL' ? 'FAIL' : effectiveVerdict === 'PASS' ? 'PASS' : 'Incomplete'}
                  </p>
                  <p className="text-[10px] font-black uppercase opacity-60 tracking-widest mt-0.5">
                    {effectiveVerdict === 'FAIL' ? 'Compliance Violations' : effectiveVerdict === 'PASS' ? 'No Compliance Flags' : 'In Review'}
                  </p>
                  {effectiveVerdictReason && (
                    <p className="text-xs mt-2 opacity-80 leading-relaxed font-medium">{effectiveVerdictReason}</p>
                  )}
                </div>
              </div>
            )}

            {/* Compliance Summary — shown when DB flags are loaded or evaluator data available */}
            {(dbFlags !== null || compliance) && (
              <div className="bg-white/40 dark:bg-slate-900/40 border border-slate-200 dark:border-white/5 rounded-2xl p-8 shadow-sm">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary">
                    <span className="material-symbols-outlined text-[24px]">verified_user</span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white tracking-tight uppercase tracking-widest">Compliance Report</h3>
                </div>

                {/* Stats — use DB counts as authoritative, total_checks from evaluator */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                  <div className="bg-slate-50/50 dark:bg-white/5 border border-slate-100 dark:border-white/5 p-4 rounded-2xl text-center">
                    <span className="block text-2xl font-black text-slate-900 dark:text-white">{compliance?.total_checks ?? 7}</span>
                    <span className="block text-[9px] font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest mt-1">Checks</span>
                  </div>
                  <div className={`p-4 rounded-2xl text-center border ${dbHardBlocks > 0 ? 'bg-red-50 dark:bg-red-950/20 border-red-100 dark:border-red-900/20' : 'bg-slate-50/50 dark:bg-white/5 border-slate-100 dark:border-white/5'}`}>
                    <span className={`block text-2xl font-black ${dbHardBlocks > 0 ? 'text-red-500' : 'text-slate-900 dark:text-white'}`}>
                      {dbHardBlocks}
                    </span>
                    <span className="block text-[9px] font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest mt-1">Blocks</span>
                  </div>
                  <div className={`p-4 rounded-2xl text-center border ${dbSoftWarnings > 0 ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-100 dark:border-amber-900/20' : 'bg-slate-50/50 dark:bg-white/5 border-slate-100 dark:border-white/5'}`}>
                    <span className={`block text-2xl font-black ${dbSoftWarnings > 0 ? 'text-amber-500' : 'text-slate-900 dark:text-white'}`}>
                      {dbSoftWarnings}
                    </span>
                    <span className="block text-[9px] font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest mt-1">Warnings</span>
                  </div>
                </div>

                {/* Individual Flag Cards — DB flags are authoritative; enriched with evaluator timestamps where available */}
                <div className="space-y-4">
                  {activeDbFlags.length === 0 ? (
                    <div className="flex items-center gap-4 p-5 bg-emerald-50/50 dark:bg-emerald-950/10 border border-emerald-100 dark:border-emerald-900/20 rounded-2xl">
                      <span className="material-symbols-outlined text-emerald-500">check_circle</span>
                      <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">All compliance checks passed</span>
                    </div>
                  ) : (
                    activeDbFlags.map((dbFlag) => {
                      const evaluatorFlag = evaluatorFlagByType.get(dbFlag.flagType);
                      const hasTimestamp = evaluatorFlag?.timestamp_start_seconds != null;
                      const styles = SEVERITY_STYLES[dbFlag.severity] ?? SEVERITY_STYLES['HARD_BLOCK'];
                      const excerpt = dbFlag.transcriptExcerpt ?? evaluatorFlag?.transcript_excerpt ?? null;

                      const phraseDetails = dbFlag.flagType === 'REQUIRED_PHRASE_MISSING'
                        ? (evaluatorFlag?.details as {
                            phrase_count?: number;
                            required_count?: number;
                            matched_count?: number;
                            missing_count?: number;
                            missing_phrases?: Array<{ phrase: string }>;
                            matched_phrases?: Array<{ phrase: string }>;
                          } | null) ?? null
                        : null;

                      return (
                        <div
                          key={dbFlag.id}
                          className={`p-5 rounded-2xl border transition-all ${styles.bg} ${styles.border} ${hasTimestamp ? 'cursor-pointer hover:scale-[1.01] active:scale-[0.99]' : ''}`}
                          onClick={hasTimestamp ? () => seekTo(evaluatorFlag!.timestamp_start_seconds!) : undefined}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <span className={`text-[10px] font-black uppercase tracking-widest ${styles.text}`}>
                              {dbFlag.flagType.replace(/_/g, ' ')}
                            </span>
                            <div className="flex items-center gap-3">
                              {evaluatorFlag?.timestamp_start && (
                                <span className="font-mono text-[9px] font-bold text-slate-400 dark:text-slate-600 bg-white dark:bg-slate-800 px-2 py-0.5 rounded-md border border-slate-100 dark:border-slate-700">{evaluatorFlag.timestamp_start}</span>
                              )}
                            </div>
                          </div>

                          {/* Required phrase breakdown — only for REQUIRED_PHRASE_MISSING */}
                          {phraseDetails && (
                            <div className="mb-3 space-y-3">
                              {/* Count summary */}
                              <div className="space-y-2">
                                <div className="flex flex-wrap gap-2">
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-100/60 dark:bg-emerald-950/30 border border-emerald-200/60 dark:border-emerald-900/30 text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-400">
                                    <span className="material-symbols-outlined text-[13px]">check_circle</span>
                                    {phraseDetails.matched_count ?? 0} spoken
                                  </span>
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-100/60 dark:bg-red-950/30 border border-red-200/60 dark:border-red-900/30 text-[10px] font-black uppercase tracking-widest text-red-600 dark:text-red-400">
                                    <span className="material-symbols-outlined text-[13px]">crisis_alert</span>
                                    {phraseDetails.missing_count ?? 0} mandatory missed
                                  </span>
                                </div>
                                <p className="text-[10px] font-semibold text-slate-400 dark:text-slate-600">
                                  Threshold: {phraseDetails.required_count ?? 0} of {phraseDetails.phrase_count ?? 0} configured phrases must be spoken
                                </p>
                              </div>

                              {/* Undetected phrases */}
                              {(phraseDetails.missing_phrases?.length ?? 0) > 0 && (
                                <div className="space-y-1.5">
                                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">
                                    Not detected ({phraseDetails.missing_phrases!.length})
                                  </p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {phraseDetails.missing_phrases!.map(({ phrase }) => (
                                      <span
                                        key={phrase}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-red-100/60 dark:bg-red-950/30 border border-red-200/60 dark:border-red-900/30 text-[10px] font-semibold text-red-600 dark:text-red-400"
                                      >
                                        <span className="material-symbols-outlined text-[11px]">close</span>
                                        {phrase}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Matched phrases */}
                              {(phraseDetails.matched_phrases?.length ?? 0) > 0 && (
                                <div className="space-y-1.5">
                                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">
                                    Spoken ({phraseDetails.matched_phrases!.length})
                                  </p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {phraseDetails.matched_phrases!.map(({ phrase }) => (
                                      <span
                                        key={phrase}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-100/60 dark:bg-emerald-950/30 border border-emerald-200/60 dark:border-emerald-900/30 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400"
                                      >
                                        <span className="material-symbols-outlined text-[11px]">check</span>
                                        {phrase}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {excerpt && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 italic mb-3 leading-relaxed border-l-2 border-slate-200 dark:border-slate-800 pl-4">
                              &ldquo;{excerpt}&rdquo;
                            </p>
                          )}

                          {hasTimestamp && (
                            <div className="flex items-center gap-1.5 text-primary">
                              <span className="material-symbols-outlined text-[16px]">play_circle</span>
                              <span className="text-[10px] font-black uppercase tracking-widest">Rewind to check</span>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* Pipeline Error */}
            {pipeline?.hasErrors && (
              <div className="bg-red-50 dark:bg-red-950/10 border border-red-100 dark:border-red-900/20 rounded-2xl p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center text-red-500">
                    <span className="material-symbols-outlined">error_outline</span>
                  </div>
                  <h3 className="text-lg font-bold text-red-600 dark:text-red-400 tracking-tight uppercase tracking-widest">Evaluator Critical Error</h3>
                </div>
                <div className="space-y-4">
                  {pipeline.steps.filter((s) => !s.success).map((step, i) => (
                    <div key={i} className="bg-white/50 dark:bg-black/20 rounded-2xl p-5 border border-red-100 dark:border-red-900/10">
                      <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mb-2">
                        Failed Step: {step.step.replace(/-/g, ' ')}
                      </p>
                      <p className="text-xs text-slate-600 dark:text-slate-400 font-mono break-all leading-relaxed">{step.error}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Metadata Section */}
            {evaluatorOutput && (
              <div className="bg-white/40 dark:bg-slate-900/40 border border-slate-200 dark:border-white/5 rounded-2xl p-8 shadow-sm">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary">
                    <span className="material-symbols-outlined text-[24px]">info</span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white tracking-tight uppercase tracking-widest">Asset Metadata</h3>
                </div>
                <div className="space-y-6">
                  {evaluatorOutput.duration_seconds != null && (
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-slate-50/50 dark:bg-white/5 p-5 rounded-2xl gap-3">
                      <span className="text-[10px] font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest">Extracted Duration</span>
                      <span className="text-sm font-bold text-slate-900 dark:text-white bg-white dark:bg-slate-800 px-3 py-1 rounded-lg border border-slate-100 dark:border-slate-700">{Math.round(evaluatorOutput.duration_seconds)} seconds</span>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-slate-50/50 dark:bg-white/5 p-5 rounded-2xl gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 min-w-10 rounded-xl bg-white dark:bg-slate-800 flex items-center justify-center text-slate-400 shadow-sm border border-slate-100 dark:border-slate-700">
                        <span className="material-symbols-outlined text-[20px]">description</span>
                      </div>
                      <span className="text-[10px] font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest leading-none">Speech Transcript Analysis</span>
                    </div>
                    {evaluatorOutput.has_transcript && evaluatorOutput.transcript ? (
                      <button
                        type="button"
                        onClick={() => setShowTranscript(true)}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-white text-[10px] font-black tracking-widest uppercase hover:scale-[1.02] shadow-lg shadow-primary/20 transition-all shrink-0"
                      >
                        <span className="material-symbols-outlined text-[16px]">file_open</span>
                        Read Analysis
                      </button>
                    ) : (
                      <span className="text-xs font-bold text-slate-300 dark:text-slate-700 bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-lg">None Extracted</span>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* Coaching Panel */}
            {(coachingNotes.length > 0 || userRole === UserRole.TENANT_ADMIN) && (
              <div className="bg-white/40 dark:bg-slate-900/40 border border-slate-200 dark:border-white/5 p-8">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-10 h-10 bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                    <span className="material-symbols-outlined text-[24px]">school</span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white tracking-tight uppercase tracking-widest">Coaching</h3>
                  {coachingNotes.filter((n) => !n.isAcknowledged).length > 0 && (
                    <span className="ml-auto text-[10px] font-black uppercase tracking-widest bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-2 py-1">
                      {coachingNotes.filter((n) => !n.isAcknowledged).length} new
                    </span>
                  )}
                </div>

                {/* Add note form — TENANT_ADMIN only */}
                {userRole === UserRole.TENANT_ADMIN && (
                  <div className="mb-6">
                    <textarea
                      value={newNoteText}
                      onChange={(e) => setNewNoteText(e.target.value)}
                      placeholder="Add a coaching note for this rep (min 20 characters)..."
                      rows={3}
                      className="w-full bg-slate-50/50 dark:bg-white/5 border-0 border-b border-slate-200 dark:border-white/10 px-4 py-3 text-sm text-slate-700 dark:text-slate-300 placeholder-slate-400 dark:placeholder-slate-600 resize-none focus:outline-none focus:border-primary transition-colors"
                    />
                    <button
                      type="button"
                      onClick={handleAddNote}
                      disabled={submittingNote || newNoteText.trim().length < 20}
                      className="mt-3 w-full h-10 bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-black uppercase tracking-widest transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {submittingNote ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white animate-spin rounded-full" />
                      ) : (
                        <>
                          <span className="material-symbols-outlined text-[16px]">add</span>
                          Add Note
                        </>
                      )}
                    </button>
                  </div>
                )}

                {coachingLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="w-5 h-5 border-2 border-emerald-500/30 border-t-emerald-500 animate-spin rounded-full" />
                  </div>
                ) : coachingNotes.length === 0 ? (
                  <p className="text-xs text-slate-400 dark:text-slate-600 font-medium text-center py-4">No coaching notes yet for this video.</p>
                ) : (
                  <div className="space-y-4">
                    {coachingNotes.map((note) => (
                      <div
                        key={note.id}
                        className={`p-5 border transition-all ${
                          note.isAcknowledged
                            ? 'bg-slate-50/30 dark:bg-white/2 border-slate-100 dark:border-white/5 opacity-60'
                            : 'bg-emerald-50/50 dark:bg-emerald-950/10 border-emerald-100 dark:border-emerald-900/20'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 ${
                              note.source === 'AUTO'
                                ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                                : 'bg-slate-200/50 dark:bg-white/10 text-slate-500 dark:text-slate-400'
                            }`}>
                              {note.source === 'AUTO' ? 'Auto' : 'Manager'}
                            </span>
                            {note.signalId != null && (
                              <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                                {SIGNAL_NAMES[note.signalId] ?? `Signal ${note.signalId}`}
                              </span>
                            )}
                          </div>
                          {note.isAcknowledged && (
                            <span className="flex items-center gap-1 text-[9px] font-black text-emerald-500 uppercase tracking-widest">
                              <span className="material-symbols-outlined text-[14px]">check_circle</span>
                              Done
                            </span>
                          )}
                        </div>

                        <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-4">{note.noteText}</p>

                        {!note.isAcknowledged && note.repId === userId && (
                          <button
                            type="button"
                            onClick={() => handleAcknowledge(note.id)}
                            disabled={acknowledging === note.id}
                            className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors disabled:opacity-50"
                          >
                            {acknowledging === note.id ? (
                              <div className="w-3.5 h-3.5 border-2 border-emerald-500/30 border-t-emerald-500 animate-spin rounded-full" />
                            ) : (
                              <span className="material-symbols-outlined text-[16px]">check_circle</span>
                            )}
                            Mark as done
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-200">
          <div
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-10 space-y-8 text-center max-w-md w-full rounded-2xl shadow-2xl relative animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto p-4 bg-red-50 dark:bg-red-950/30 rounded-full w-fit">
              <span className="material-symbols-outlined text-[32px] text-red-500 dark:text-red-400 block">delete</span>
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">Delete Asset</h3>
              <p className="text-slate-500 dark:text-slate-400 font-medium text-sm leading-relaxed">
                Are you sure you want to delete this video asset?<br />
                This action is permanent and cannot be undone.
              </p>
            </div>
            <div className="flex items-center gap-3 pt-4">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="flex-1 h-12 rounded-xl font-bold border border-red-100 dark:border-red-900/30 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all cursor-pointer text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 h-12 rounded-xl font-bold bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white shadow-lg shadow-red-500/20 dark:shadow-red-900/40 transition-all cursor-pointer text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {deleting ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white animate-spin rounded-full" />
                ) : (
                  'Delete Asset'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transcript Modal */}
      {showTranscript && evaluatorOutput?.transcript && (
        <TranscriptModal
          transcript={evaluatorOutput.transcript}
          onClose={() => setShowTranscript(false)}
        />
      )}
    </div>
  );
};

export default VideoDetailPage;
